import { createAsyncThunk } from '@reduxjs/toolkit';

import callApi from '../../service/callApi';
import { somethingWrongNotification } from '../notifier/notifierActions';
import sendRequest from '../../service/sendRequest';
import { ASSISTANTS_CATEGORY, GENIE_EVALUATE_TYPE } from '../../constants';
import { history } from '../../store';
import { cancelEvaluationByType, setEvalAbortController, updateEvaluatedGeneratedMessages } from './genieGoReducer';

const GET_GENIE_DATA = 'genieGo/GET_GENIE_DATA';
const PREDICT_GENIE_GO_RESPONSE = 'genieGo/PREDICT_GENIE_GO_RESPONSE';
const RESET_SESSION = 'genieGo/RESET_SESSION';
const REPLY_GENIE_GO = 'genieGo/REPLY_GENIE_GO';
const GENERATE_GENIE_GO_MESSAGES = 'genieGo/GENERATE_GENIE_GO_MESSAGES';
const EVALUATE_GENERATED_MESSAGES = 'genieGo/EVALUATE_GENERATED_MESSAGES';

export const getGenieGoData = createAsyncThunk(GET_GENIE_DATA, async ({ id }, { rejectWithValue }) => {
  const response = await sendRequest(() => callApi.get(`/api/genie2go/${id}`), {
    onError: (e) => {
      history.push('/projects');
      return rejectWithValue(e);
    },
  });

  return { projectId: id, ...response.data };
});

export const predictGenieGoResponse = createAsyncThunk(
  PREDICT_GENIE_GO_RESPONSE,
  async ({ values, projectId }, { rejectWithValue }) => {
    const response = await sendRequest(
      () => callApi.post(`/api/genie2go/predict/messages?id=${projectId}`, { ...values }),
      { onError: (e) => rejectWithValue(e) },
    );

    return response.data;
  },
);

export const resetSession = createAsyncThunk(RESET_SESSION, async (_, { rejectWithValue, getState }) => {
  const threadId = getState().genieGo.threadId;

  if (!threadId) {
    return rejectWithValue(null);
  }

  const response = await sendRequest(() => callApi.post(`/api/ai/playground/open-ai/reset?threadId=${threadId}`), {
    onError: (e) => rejectWithValue(e),
  });

  return response.data;
});

export const replyGenieGo = createAsyncThunk(
  REPLY_GENIE_GO,
  async ({ id, reply, assistantId, generateFormValues }, { rejectWithValue, getState, dispatch }) => {
    const { url } = generateFormValues;
    const storedThreadId = getState().genieGo.threadId;

    dispatch(cancelEvaluationByType(GENIE_EVALUATE_TYPE.GENERATED));

    const response = await sendRequest(
      () =>
        callApi.post(`/api/genie2go/reply?id=${id}`, {
          reply,
          assistantId,
          threadId: storedThreadId,
          url,
        }),
      {
        onError: (e) => rejectWithValue(e),
      },
    );

    const storedProjectId = getState().genieGo.projectId;

    if (id !== storedProjectId) {
      return rejectWithValue(null);
    }

    dispatch(
      evaluateGeneratedMessages({ values: generateFormValues, projectId: id, messages: response.data.messages }),
    );

    return response.data;
  },
);

export const generateGenieGoMessages = createAsyncThunk(
  GENERATE_GENIE_GO_MESSAGES,
  async ({ id, values }, { rejectWithValue, dispatch, getState }) => {
    const { byPass, focusOn, url, channels, fileText } = values;

    const assistantId = channels[ASSISTANTS_CATEGORY.GENERATE_ALL].assistant?.id;

    if (!id || !assistantId || !url) {
      return rejectWithValue(null);
    }

    dispatch(cancelEvaluationByType(GENIE_EVALUATE_TYPE.GENERATED));

    const response = await sendRequest(
      (cancelToken) =>
        callApi.post(
          `/api/genie2go/url?id=${id}`,
          {
            assistantId,
            byPass,
            focusOn,
            url,
            fileText,
          },
          { cancelToken },
        ),
      {
        onError: (e) => {
          dispatch(somethingWrongNotification());
          return rejectWithValue(e);
        },
      },
    );

    const storedProjectId = getState().genieGo.projectId;

    if (id !== storedProjectId) {
      return rejectWithValue(null);
    }

    dispatch(evaluateGeneratedMessages({ values, projectId: id, messages: response.data.messages }));

    return response.data;
  },
);

export const evaluateGeneratedMessages = createAsyncThunk(
  EVALUATE_GENERATED_MESSAGES,
  async ({ values, projectId, messages }, { rejectWithValue, dispatch }) => {
    const { url } = values;

    const controller = new AbortController();
    dispatch(setEvalAbortController({ controller, type: GENIE_EVALUATE_TYPE.GENERATED }));

    try {
      const response = await sendRequest(
        () =>
          callApi.post(`/api/genie2go/predict/bunch?projectId=${projectId}`, {
            messages,
            url,
          }),
        {
          onError: (e) => {
            throw new Error(e);
          },
        },
      );

      dispatch(updateEvaluatedGeneratedMessages(response.data));
      dispatch(setEvalAbortController({ controller: null, type: GENIE_EVALUATE_TYPE.GENERATED }));
    } catch (error) {
      dispatch(cancelEvaluationByType(GENIE_EVALUATE_TYPE.GENERATED));

      if (controller.signal.aborted) return rejectWithValue('Cancelled');
      return rejectWithValue(error.message);
    }
  },
);
